import api from './api'

export type DocumentType =
  | 'terms_conditions'
  | 'free_sim_terms'
  | 'privacy_policy'
  | 'cookie_policy'
  | 'acceptable_use'
  | 'fair_use_policy'
  | 'repairs_replacements'
  | 'website_terms'
  | 'complaints_code'
  | 'early_termination'

export interface LegalDocument {
  id: number
  document_type: DocumentType
  version: string
  title: string
  content_html: string
  is_active: boolean
  effective_date?: string
  created_at: string
  updated_at?: string
  original_filename: string
  file_path: string
  file_size?: number
  uploaded_by_id?: number
  change_summary?: string
}

export interface DocumentVersion {
  id: number
  document_type: DocumentType
  version: string
  title: string
  is_active: boolean
  effective_date?: string
  created_at: string
  original_filename: string
  change_summary?: string
}

export interface DocumentAcceptance {
  user_id: number
  user_email: string
  accepted_at: string
  ip_address?: string
}

export interface DocumentTypeInfo {
  value: string
  label: string
}

export const legalDocumentsService = {
  // Public endpoints
  async getActiveDocument(documentType: DocumentType): Promise<LegalDocument> {
    const response = await api.get(`/legal-documents/active/${documentType}`)
    return response.data
  },

  async getAllVersions(documentType: DocumentType): Promise<DocumentVersion[]> {
    const response = await api.get(`/legal-documents/all/${documentType}`)
    return response.data
  },

  async getDocumentById(documentId: number): Promise<LegalDocument> {
    const response = await api.get(`/legal-documents/${documentId}`)
    return response.data
  },

  async acceptDocument(documentId: number): Promise<{ message: string; accepted_at: string }> {
    const response = await api.post(`/legal-documents/accept/${documentId}`)
    return response.data
  },

  async getDocumentTypes(): Promise<{ document_types: DocumentTypeInfo[] }> {
    const response = await api.get('/legal-documents/types/all')
    return response.data
  },

  // Admin endpoints
  async uploadDocument(data: FormData): Promise<{
    message: string
    id: number
    document_type: DocumentType
    version: string
    is_active: boolean
  }> {
    const response = await api.post('/legal-documents/upload', data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  },

  async activateVersion(documentId: number): Promise<{
    message: string
    document_type: DocumentType
    version: string
  }> {
    const response = await api.put(`/legal-documents/${documentId}/activate`)
    return response.data
  },

  async deleteVersion(documentId: number): Promise<{ message: string }> {
    const response = await api.delete(`/legal-documents/${documentId}`)
    return response.data
  },

  async downloadDocument(documentId: number): Promise<Blob> {
    const response = await api.get(`/legal-documents/${documentId}/download`, {
      responseType: 'blob',
    })
    return response.data
  },

  async getAcceptances(documentId: number): Promise<DocumentAcceptance[]> {
    const response = await api.get(`/legal-documents/${documentId}/acceptances`)
    return response.data
  },

  async copyEditDocument(
    sourceDocumentId: number,
    data: {
      version: string
      title: string
      content_html: string
      change_summary?: string
      effective_date?: string
      make_active: boolean
    }
  ): Promise<{ message: string; id: number; document_type: DocumentType; version: string; is_active: boolean }> {
    const response = await api.post(`/legal-documents/${sourceDocumentId}/copy-edit`, data)
    return response.data
  },
}
